import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, ChevronDown, ChevronUp, RefreshCw, Zap, ScrollText, X, ChevronDown as Chevron } from 'lucide-react';
import { ACCESS_LEVELS, PromptOverrides, AiPromptTemplate } from '@rti/shared';
import { apiClient } from '@rti/shared';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';

interface AIPanelData {
  brief?: string | null;
  summary?: string | null;
  generated_at?: string;
  configured?: boolean;
}

interface AIPanelProps {
  fetchFn: (force?: boolean) => Promise<AIPanelData>;
  regenerateFn?: (overrides: PromptOverrides) => Promise<AIPanelData>;
  context?: string;
  resetKey?: string;
  enabled?: boolean;
}

const isStaleAfter830CST = (generatedAt: string): boolean => {
  const now = new Date();
  const cstNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  const cutoff = new Date(cstNow);
  cutoff.setHours(8, 30, 0, 0);
  if (cstNow < cutoff) return false;
  const genCst = new Date(new Date(generatedAt).toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  return genCst < cutoff;
};

const AIPanel: React.FC<AIPanelProps> = ({ fetchFn, regenerateFn, context, resetKey, enabled = true }) => {
  const aiEnabled = useFeatureFlag('acc-ai-summary');
  const { user } = useAuth();
  const { isDark } = useTheme();
  const isEmployee = !!user && (user.effective_membership_id ?? 0) >= ACCESS_LEVELS.EMPLOYEE;

  const [isExpanded, setIsExpanded] = useState(false);
  const [data, setData] = useState<AIPanelData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [activeTab, setActiveTab] = useState<'brief' | 'detailed'>('brief');

  const [showModal, setShowModal] = useState(false);
  const [modalTemplate, setModalTemplate] = useState<AiPromptTemplate | null>(null);
  const [modalLoadingTemplate, setModalLoadingTemplate] = useState(false);
  const [editInstructions, setEditInstructions] = useState('');
  const [editSystemMessage, setEditSystemMessage] = useState('');
  const [extraContext, setExtraContext] = useState('');
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  const fetchFnRef = useRef(fetchFn);
  const regenerateFnRef = useRef(regenerateFn);
  useEffect(() => { fetchFnRef.current = fetchFn; });
  useEffect(() => { regenerateFnRef.current = regenerateFn; });

  const fetch = async (force = false) => {
    setIsLoading(true);
    setError(false);
    try {
      const result = await fetchFnRef.current(force);
      if (result.configured === false) {
        setConfigured(false);
        return;
      }
      if (!force && result.generated_at && isStaleAfter830CST(result.generated_at)) {
        const fresh = await fetchFnRef.current(true);
        setData(fresh.configured === false ? result : fresh);
      } else {
        setData(result);
      }
    } catch {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (aiEnabled && isExpanded && !data && !error) {
      fetch();
    }
  }, [aiEnabled, isExpanded, data, error]);

  useEffect(() => {
    setData(null);
    setError(false);
  }, [resetKey]);

  const openPromptModal = async () => {
    if (!context || !regenerateFnRef.current) {
      fetch(true);
      return;
    }
    setModalLoadingTemplate(true);
    setShowModal(true);
    setExtraContext('');
    setSaveAsDefault(false);
    setShowAdvanced(false);
    try {
      const tpl = await apiClient.getAiPromptTemplate(context);
      setModalTemplate(tpl);
      setEditInstructions(tpl.instructions);
      setEditSystemMessage(tpl.system_message);
    } catch {
      setModalTemplate(null);
    } finally {
      setModalLoadingTemplate(false);
    }
  };

  const handleGenerate = async () => {
    if (!regenerateFnRef.current) return;
    setIsRegenerating(true);
    try {
      const overrides: PromptOverrides = {
        extra_context: extraContext.trim() || undefined,
        save_as_default: saveAsDefault,
      };
      if (showAdvanced && modalTemplate) {
        if (editSystemMessage !== modalTemplate.system_message) overrides.system_message = editSystemMessage;
        if (editInstructions !== modalTemplate.instructions) overrides.instructions = editInstructions;
      }
      const result = await regenerateFnRef.current(overrides);
      if (result.configured === false) {
        setConfigured(false);
      } else {
        setData(result);
      }
      setShowModal(false);
    } catch {
      setError(true);
      setShowModal(false);
    } finally {
      setIsRegenerating(false);
    }
  };

  if (!aiEnabled || !configured || !enabled) return null;

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return iso;
    }
  };

  const hasBrief = !!data?.brief;
  const hasDetailed = !!data?.summary;

  return (
    <>
      <div className="border border-rs-jet rounded-lg mb-5">
        <button
          className="w-full flex items-center justify-between px-4 py-3 text-left"
          onClick={() => setIsExpanded(prev => !prev)}
        >
          <div className="flex items-center gap-2">
            <Sparkles size={20} className="text-rs-primary" />
            <span className="text-rs-off-white">AI Powered Insights</span>
            <span className="text-rs-dark-green italic bg-rs-jet pr-1.5 py-0.5 rounded">Beta</span>
          </div>
          {isExpanded ? (
            <ChevronUp size={15} className="text-rs-off-white opacity-60" />
          ) : (
            <ChevronDown size={15} className="text-rs-off-white opacity-60" />
          )}
        </button>

        {isExpanded && (
          <div className="px-4 pb-4">
            {isLoading && (
              <div className="flex flex-col items-center gap-3 py-6">
                <div className="relative flex items-center justify-center w-10 h-10">
                  <div className="absolute inset-0 rounded-full border border-rs-primary opacity-20 animate-ping" />
                  <div className="absolute inset-1 rounded-full border border-rs-primary opacity-10 animate-ping" style={{ animationDelay: '0.3s' }} />
                  <Sparkles size={18} className="text-rs-primary animate-pulse" />
                </div>
                <div className="flex flex-col items-center gap-2">
                  <p className="text-xs text-rs-off-white opacity-40 tracking-widest uppercase">Generating insights</p>
                  <div className="flex gap-1.5">
                    {[0, 1, 2].map(i => (
                      <span
                        key={i}
                        className="w-1.5 h-1.5 rounded-full bg-rs-primary opacity-50 animate-bounce"
                        style={{ animationDelay: `${i * 0.18}s` }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {!isLoading && error && (
              <p className="text-rs-off-white opacity-50 pt-1">Unavailable at this time.</p>
            )}

            {!isLoading && (hasBrief || hasDetailed) && (
              <div>
                <div className="flex gap-1 mb-3 p-0.5 rounded-md w-fit">
                  {hasBrief && (
                    <button
                      onClick={() => setActiveTab('brief')}
                      className={`flex items-center gap-1.5 px-3 py-1 text-sm rounded transition-colors ${
                        activeTab === 'brief'
                          ? 'bg-rs-green text-black opacity-40 !cursor-not-allowed'
                          : 'bg-rs-green text-black hover:opacity-90'
                      }`}
                    >
                      <Zap size={20} />
                      Highlights
                    </button>
                  )}
                  {hasDetailed && (
                    <button
                      onClick={() => setActiveTab('detailed')}
                      className={`flex items-center gap-1.5 px-3 py-1 text-sm rounded transition-colors ${
                        activeTab === 'detailed'
                          ? 'bg-rs-green text-black opacity-40 !cursor-not-allowed'
                          : 'bg-rs-green text-black hover:opacity-90'
                      }`}
                    >
                      <ScrollText size={20} />
                      Detailed Analysis
                    </button>
                  )}
                </div>

                {activeTab === 'brief' ? (
                  <div className='p-5'>
                    <p className={`text-sm font-semibold ${isDark ? 'text-rs-green/70' : 'text-black'} uppercase tracking-widest mb-2`}>Highlights</p>
                    <ul className="flex flex-col gap-1.5">
                      {(data?.brief ?? '').split('\n').filter(l => l.trim()).map((line, i) => (
                        <li key={i} className="flex items-start gap-2 text-rs-off-white leading-relaxed ml-2">
                          <span className="text-rs-primary mt-0.5 shrink-0">–</span>
                          <span>{line.replace(/^[-–]\s*/, '')}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className='p-5'>
                    <p className={`text-sm font-semibold ${isDark ? 'text-rs-green/70' : 'text-black'} uppercase tracking-widest mb-2`}>Detailed Analysis</p>
                    <p className=" text-rs-off-white leading-relaxed whitespace-pre-line">{data?.summary}</p>
                  </div>
                )}

                <div className="flex items-center justify-between mt-3 pt-3 border-t border-rs-jet">
                  <span className={`text-xs ${isDark ? 'text-rs-off-white opacity-40' : 'text-black'}`}>
                    AI generated · {data?.generated_at ? formatDate(data.generated_at) : ''}
                  </span>
                  {isEmployee && (
                    <button
                      className={`flex items-center gap-1 text-xs ${isDark ? 'text-rs-off-white opacity-50' : 'text-black'} hover:opacity-80`}
                      onClick={openPromptModal}
                      disabled={isLoading}
                    >
                      <RefreshCw size={11} />
                      Regenerate
                    </button>
                  )}
                </div>
                <p className={`text-sm ${isDark ? 'text-rs-off-white/50' : 'text-black'}  mt-2 leading-relaxed`}>
                  AI-generated content may contain errors or omissions. This insight is intended as a starting point for analysis only and should not be used as the sole basis for business decisions. Always verify against source data.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="bg-rs-surface border border-rs-jet rounded-xl w-full max-w-2xl shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-rs-jet">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-rs-primary" />
                <span className="text-rs-off-white font-medium">Regenerate AI Insights</span>
              </div>
              <button onClick={() => setShowModal(false)} className="text-rs-off-white opacity-50 hover:opacity-80">
                <X size={16} />
              </button>
            </div>

            <div className="px-5 py-4 flex flex-col gap-4">
              {modalLoadingTemplate ? (
                <div className="flex justify-center py-4">
                  <div className="w-5 h-5 border-2 border-rs-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  <div>
                    <label className={`block text-xs ${isDark ? 'text-rs-off-white opacity-60' : 'text-black'} mb-1.5 uppercase tracking-wider`}>Additional Context</label>
                    <textarea
                      className="w-full bg-rs-jet border border-rs-jet rounded-lg px-3 py-2 text-sm text-rs-off-white placeholder-rs-off-white/30 resize-none focus:outline-none focus:border-rs-primary/50 h-30"
                      placeholder="e.g. Focus on benzene dynamics; the Q4 data looks unusual"
                      value={extraContext}
                      onChange={e => setExtraContext(e.target.value)}
                    />
                  </div>

                  <div>
                    <button
                      className={`flex items-center gap-1.5 text-xs ${isDark ? 'text-rs-off-white opacity-50' : 'text-black'} hover:opacity-80`}
                      onClick={() => setShowAdvanced(p => !p)}
                    >
                      <Chevron size={12} className={`transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                      Edit prompt template
                    </button>

                    {showAdvanced && (
                      <div className="mt-3 flex flex-col gap-3">
                        <div>
                          <label className={`block text-xs ${isDark ? 'text-rs-off-white opacity-60' : 'text-black'} mb-1.5 uppercase tracking-wider`}>System Message</label>
                          <textarea
                            className="w-full bg-rs-jet border border-rs-jet rounded-lg px-3 py-2 text-xs text-rs-off-white/70 font-mono resize-none focus:outline-none focus:border-rs-primary/50 h-35"
                            value={editSystemMessage}
                            onChange={e => setEditSystemMessage(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className={`block text-xs ${isDark ? 'text-rs-off-white opacity-60' : 'text-black'} mb-1.5 uppercase tracking-wider`}>Instructions</label>
                          <textarea
                            className="w-full bg-rs-jet border border-rs-jet rounded-lg px-3 py-2 text-xs text-rs-off-white/70 font-mono resize-none focus:outline-none focus:border-rs-primary/50 h-75"
                            value={editInstructions}
                            onChange={e => setEditInstructions(e.target.value)}
                          />
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="accent-rs-primary w-3.5 h-3.5"
                            checked={saveAsDefault}
                            onChange={e => setSaveAsDefault(e.target.checked)}
                          />
                          <span className={`text-xs ${isDark ? 'text-rs-off-white opacity-60' : 'text-black'}`}>Save as default for future generations</span>
                        </label>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-rs-jet">
              <button
                onClick={() => setShowModal(false)}
                className={`px-4 py-1.5 text-sm ${isDark ? 'text-rs-off-white opacity-50' : 'text-black'} hover:opacity-80`}
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={isRegenerating || modalLoadingTemplate}
                className={`flex items-center gap-1.5 px-4 py-1.5 text-sm bg-rs-dark-green ${isDark ? 'text-rs-off-black' : 'text-black'} rounded-lg hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                {isRegenerating ? (
                  <div className="w-3.5 h-3.5 border-2 border-rs-off-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Sparkles size={14} />
                )}
                {isRegenerating ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIPanel;
